import "../lib/buffer-polyfill";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  ClientOnly,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { WalletProvider } from "@/components/WalletProvider";
import { ConnectButton, ChainMismatchBanner } from "@/components/ConnectButton";
import { Toaster } from "@/components/ui/sonner";
import logoAsset from "@/assets/robotdex-logo.jpg.asset.json";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold neon-text">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link to="/" className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatErrorDetails(error: Error) {
  return JSON.stringify({
    title: "RobotDex client route error",
    timestamp: new Date().toISOString(),
    path: typeof window === "undefined" ? "SSR" : window.location.pathname,
    name: error.name,
    message: error.message,
    stack: error.stack,
  }, null, 2);
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => { reportLovableError(error, { boundary: "tanstack_root_error_component" }); }, [error]);
  const errorDetails = formatErrorDetails(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-2xl text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Copy the error and send it in chat.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={() => { router.invalidate(); reset(); }} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Try again
          </button>
          <button onClick={() => navigator.clipboard.writeText(errorDetails)} className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent">
            Copy error
          </button>
          <a href="/" className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent">Go home</a>
        </div>
        <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 text-left text-xs text-muted-foreground">{errorDetails}</pre>
      </div>
    </div>
  );
}

function ClientErrorLogger() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => console.error("RobotDex window error", event.error ?? event.message);
    const onRejection = (event: PromiseRejectionEvent) => console.error("RobotDex unhandled rejection", event.reason);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "RobotDex - Fully On-Chain NFT Marketplace" },
      { name: "description", content: "Create, mint, buy, sell and list NFTs on Robinhood Chain and all EVM networks. Fully on-chain. No middlemen." },
      { property: "og:title", content: "RobotDex - Fully On-Chain NFT Marketplace" },
      { property: "og:description", content: "Create, mint, buy, sell and list NFTs on Robinhood Chain and all EVM networks. Fully on-chain. No middlemen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "RobotDex - Fully On-Chain NFT Marketplace" },
      { name: "twitter:description", content: "Create, mint, buy, sell and list NFTs on Robinhood Chain and all EVM networks. Fully on-chain. No middlemen." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/8d69e407-0b99-4bcc-804d-0504610f835d/id-preview-5556cf7c--6ae9d6cb-3e3f-4ef3-ac63-d04d56a97d2e.lovable.app-1783724522069.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/8d69e407-0b99-4bcc-804d-0504610f835d/id-preview-5556cf7c--6ae9d6cb-3e3f-4ef3-ac63-d04d56a97d2e.lovable.app-1783724522069.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ClientErrorLogger />
      <WalletProvider>
        <SidebarProvider>
          <div className="min-h-screen flex w-full bg-background">
            <AppSidebar />
            <div className="flex-1 flex flex-col min-w-0">
              <header className="h-14 flex items-center justify-between border-b border-border/60 px-3 backdrop-blur-md bg-background/60 sticky top-0 z-40">
                <div className="flex items-center gap-2">
                  <SidebarTrigger />
                  <span className="text-sm text-muted-foreground hidden sm:inline">Robinhood Chain · EVM</span>
                </div>
                <ConnectButton />
              </header>
              <ChainMismatchBanner />
              <main className="flex-1 min-w-0">
                <Outlet />
              </main>
            </div>
          </div>
          <Toaster />
        </SidebarProvider>
      </WalletProvider>
    </QueryClientProvider>
  );
}
